/**
 * VISTOERA_CANVAS_FUNCTION_CARD_INTERACTION_V2 + pointer contract (V9 media drag)
 * + CANONICAL NODE SHELL V2 (minimal collapsed static shell).
 *
 * Collapsed Shell V2: icon+title header + result/asset only; hover management
 * actions; Result click expands; Canvas blank collapses; max one expanded
 * generation node (CanvasBoard `expandedNodeId`). See `canvasNodeShellV2.ts`.
 *
 * Shared semantic class names for Canvas node pointer hit zones.
 * React Flow reads `nodrag` / `nopan` / `nowheel` at runtime; this module
 * documents the product contract and exports the canonical class strings so
 * node authors do not scatter ad-hoc combinations.
 *
 * V9 GESTURE DISAMBIGUATION CHANGE — the media viewport is no longer
 * blanket `nodrag`. Git archaeology root cause: "Global Interaction V1"
 * (commit cbfe7581) moved `nodrag` off the header dragHandle and onto the
 * ENTIRE media viewport via this class — which just relocated the "can't
 * drag by the video" bug instead of fixing it (confirmed on live Preview:
 * a real pointer drag on the media viewport moved the node 0px). The
 * actual product contract is "press-and-move beyond a small threshold
 * drags the node; press-and-release without moving is a click/hover" —
 * that is exactly what React Flow's own `nodeDragThreshold` /
 * `nodeClickDistance` props implement (see `CanvasBoard.tsx`), so the
 * media viewport is now a normal drag surface and click/dblclick/hover
 * keep working below that movement threshold, instead of a per-node
 * hand-rolled pointer-tracking primitive.
 *
 * | Zone              | Classes                       | Drag | Wheel | Pan |
 * |-------------------|--------------------------------|------|-------|-----|
 * | Drag surface      | (default - no nodrag)          | yes  | yes*  | yes |
 * | Presentation body | node-presentation-surface       | yes+ | yes*  | yes |
 * | Media viewport    | node-media-surface              | yes+ | yes*  | yes |
 * | Context DnD chip  | node-context-dnd-handle (+nodrag)| no  | -     | no  |
 * | Interactive ctrl  | node-control                    | no   | no    | no  |
 * | Quick Action rail | node-quick-action (+ nodrag)    | no   | -     | no  |
 * | Connection handle | pcv-handle (React Flow)         | edge | -     | no  |
 *
 * *Wheel on the canvas pane is handled by React Flow `panOnScroll`.
 * +Drag only actually moves the node once pointer travel exceeds
 * `nodeDragThreshold`; below that it resolves as a click/hover/dblclick.
 * Interactive elements INSIDE the viewport (play icon button, native
 * modal controls, inspector inputs) still carry their own `nodrag` so they
 * never start a node-drag gesture regardless of movement.
 */

/** Media preview viewport (video, skeleton player) — normal drag surface;
 * see the V9 gesture disambiguation note above for why this is not
 * `nodrag` anymore. Click/dblclick/hover still work via the global
 * `nodeDragThreshold` / `nodeClickDistance` movement thresholds. */
export const NODE_MEDIA_SURFACE_CLASS = 'pcv-node-media-surface'

/** Read-only preview / result text — normal node drag surface (NOT nodrag).
 * Uses user-select:none so press-move resolves as node drag, not text selection. */
export const NODE_PRESENTATION_SURFACE_CLASS = 'pcv-node-presentation-surface'

/** Small affordance for node-to-node HTML5 context handoff (creative→script,
 * script→storyboard). MUST stay nodrag so the handle initiates payload DnD
 * without blocking the surrounding presentation surface from node-dragging. */
export const NODE_CONTEXT_DND_HANDLE_CLASS = 'pcv-node-context-dnd-handle nodrag'

/** Blocks node drag - buttons, inputs, selects, inspector controls. */
export const NODE_CONTROL_CLASS = 'pcv-node-control nodrag'

/** Blocks node drag - contextual workflow rail beside the card. */
export const NODE_QUICK_ACTION_CLASS = 'pcv-node-quick-action nodrag'

/** Product default: Canvas media does NOT loop unless a node opts in. */
export const CANVAS_MEDIA_DEFAULT_LOOP = false as const
